import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import Seo from "../components/Seo";
import { useCart } from "../context/CartContext";
import ShipmentTracking from "../components/ShipmentTracking";

const money = (minor) => `$${(Number(minor || 0) / 100).toFixed(2)}`;
const EMPTY_PERSONALIZATION = {
  name: "",
  short_phrase: "",
  color_change: "",
  hanger: "none",
  customer_note: "",
};

export default function QuoteCheckout() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("Checking payment status...");
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [editingLineId, setEditingLineId] = useState(null);
  const [personalization, setPersonalization] = useState(EMPTY_PERSONALIZATION);
  const [submitting, setSubmitting] = useState(false);
  const cartCleared = useRef(false);
  const { clearCart } = useCart();
  const orderId = searchParams.get("order_id");
  const accessToken = searchParams.get("access_token");

  useEffect(() => {
    const quoteId = searchParams.get("quote_id");
    const source = searchParams.get("source");
    const paymentType = searchParams.get("type") || "deposit";
    if (source !== "cart") {
      setStatus(
        quoteId
          ? `Your ${paymentType} checkout is ready. Complete the hosted Square payment to confirm the order.`
          : "This checkout page was opened without a quote reference.",
      );
      return undefined;
    }
    if (!orderId) {
      setStatus(
        "This checkout page was opened without an order reference. Your cart has not been cleared.",
      );
      return undefined;
    }
    let active = true;
    let timeoutId;
    const checkOrder = async () => {
      try {
        const response = await api.get(`/orders/${orderId}/status`, {
          params: accessToken ? { access_token: accessToken } : {},
        });
        if (!active) return;
        const order = response.data;
        if (order.status === "paid") {
          setConfirmedOrder(order);
          setStatus(
            order.message || "Payment confirmed. Thank you for your order.",
          );
          if (!cartCleared.current) {
            cartCleared.current = true;
            clearCart();
          }
          return;
        }
        if (
          [
            "expired",
            "checkout_failed",
            "canceled",
            "paid_review_required",
          ].includes(order.status)
        ) {
          setStatus(
            order.status === "paid_review_required"
              ? "Payment was received, but this order needs staff review. Your cart has not been cleared."
              : "Payment was not completed. Your cart remains intact.",
          );
          return;
        }
        setStatus("Confirming your payment with Square...");
        timeoutId = window.setTimeout(checkOrder, 2000);
      } catch (error) {
        if (active)
          setStatus(`${formatApiError(error)} Your cart has not been cleared.`);
      }
    };
    checkOrder();
    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [accessToken, clearCart, orderId, searchParams]);

  const refreshPersonalization = async () => {
    const response = await api.get(`/orders/${orderId}/personalization`, {
      params: { token: accessToken },
    });
    setConfirmedOrder(response.data);
  };

  const submitPersonalization = async (lineId) => {
    setSubmitting(true);
    try {
      await api.post(`/orders/${orderId}/personalization`, {
        token: accessToken,
        line_id: lineId,
        ...personalization,
      });
      await refreshPersonalization();
      setEditingLineId(null);
    } catch (error) {
      setStatus(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const declinePersonalization = async (lineId) => {
    setSubmitting(true);
    try {
      await api.post(`/orders/${orderId}/personalization/decline`, {
        token: accessToken,
        line_id: lineId,
      });
      await refreshPersonalization();
      setEditingLineId(null);
    } catch (error) {
      setStatus(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-16">
      <Seo title="Order Payment" description="Beyond Board Woodworking order payment and confirmation." path="/quote/checkout" noindex />
      <div className="border-y border-[#26C6D5]/25 bg-zinc-900/80 p-6 sm:p-8">
        <p className="text-xs uppercase text-[#D8A34B]">Order confirmation</p>
        <h1 className="mt-3 font-display text-3xl text-[#F5F0E6]">
          {confirmedOrder?.heading || "Payment status"}
        </h1>
        <p className="mt-5 leading-relaxed text-stone-300">{status}</p>
        {confirmedOrder && (
          <>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-stone-300">
              <span>Order: {confirmedOrder.order_id}</span>
              <span>
                {confirmedOrder.fulfillment_method === "pickup"
                  ? "Pickup"
                  : "Shipping"}
              </span>
              <span>Status: {confirmedOrder.status}</span>
              <span>Total: {money(confirmedOrder.total_minor)}</span>
            </div>
            {confirmedOrder.fulfillment_method === "shipping" && <ShipmentTracking shipping={confirmedOrder.shipping} />}
            {confirmedOrder.production_estimate && (
              <p className="mt-3 text-sm text-[#D8A34B]">
                Estimated workshop time:{" "}
                {confirmedOrder.production_estimate.minimum_business_days}–
                {confirmedOrder.production_estimate.maximum_business_days}{" "}
                business days
              </p>
            )}
            <div className="mt-8 space-y-4">
              {confirmedOrder.lines?.map((line) => (
                <article
                  key={line.line_id}
                  className="grid gap-4 border-t border-stone-100/10 pt-4 sm:grid-cols-[96px_1fr]"
                >
                  <img
                    src={line.image_url_snapshot || "/branding/bbw-logo.jpg"}
                    alt=""
                    className="h-24 w-24 object-cover"
                  />
                  <div>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <h2 className="text-lg font-semibold text-stone-100">
                          {line.product_name_snapshot}
                        </h2>
                        {line.variant_name_snapshot && (
                          <p className="text-sm text-stone-400">
                            {line.variant_name_snapshot}
                          </p>
                        )}
                      </div>
                      <span
                        className={`border px-2 py-1 text-xs ${line.production_required ? "border-[#D8A34B]/40 text-[#D8A34B]" : "border-emerald-400/40 text-emerald-300"}`}
                      >
                        {line.production_required
                          ? "Made to order"
                          : "Ready stock"}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-stone-300">
                      Quantity: {line.quantity}
                    </p>
                    {line.production_required ? (
                      <p className="mt-2 text-sm text-stone-400">
                        Production status:{" "}
                        {line.production_status.replaceAll("_", " ")}
                      </p>
                    ) : (
                      <p className="mt-2 text-sm text-stone-400">
                        Ready for fulfillment. We’ll send the next update when
                        it is on the way.
                      </p>
                    )}
                    {line.personalization_allowed &&
                      [
                        "awaiting_customer",
                        "submitted",
                        "changes_requested",
                      ].includes(line.personalization_status) && (
                        <div className="mt-4">
                          <p className="text-sm text-stone-300">
                            Small personalization is included. Major design or
                            structural changes may require confirmation before
                            production.
                          </p>
                          {editingLineId !== line.line_id ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button
                                onClick={() => {
                                  setEditingLineId(line.line_id);
                                  setPersonalization(
                                    line.personalization_request ||
                                      EMPTY_PERSONALIZATION,
                                  );
                                }}
                              >
                                Personalize My Sign — Free
                              </Button>
                              <Button
                                variant="outline"
                                disabled={submitting}
                                onClick={() =>
                                  declinePersonalization(line.line_id)
                                }
                              >
                                Make It Exactly as Shown
                              </Button>
                            </div>
                          ) : (
                            <div className="mt-3 grid gap-3">
                              <input
                                value={personalization.name || ""}
                                onChange={(event) =>
                                  setPersonalization({
                                    ...personalization,
                                    name: event.target.value,
                                  })
                                }
                                maxLength={
                                  confirmedOrder.personalization_limits?.name ||
                                  24
                                }
                                placeholder="Name"
                                className="cart-inset-field rounded-md p-2.5"
                              />
                              <input
                                value={personalization.short_phrase || ""}
                                onChange={(event) =>
                                  setPersonalization({
                                    ...personalization,
                                    short_phrase: event.target.value,
                                  })
                                }
                                maxLength={
                                  confirmedOrder.personalization_limits
                                    ?.short_phrase || 40
                                }
                                placeholder="Short phrase"
                                className="cart-inset-field rounded-md p-2.5"
                              />
                              <input
                                value={personalization.color_change || ""}
                                onChange={(event) =>
                                  setPersonalization({
                                    ...personalization,
                                    color_change: event.target.value,
                                  })
                                }
                                placeholder="Simple color change"
                                className="cart-inset-field rounded-md p-2.5"
                              />
                              <select
                                value={personalization.hanger || "none"}
                                onChange={(event) =>
                                  setPersonalization({
                                    ...personalization,
                                    hanger: event.target.value,
                                  })
                                }
                                className="cart-inset-field rounded-md p-2.5"
                              >
                                <option value="rope">Rope hanger</option>
                                <option value="metal_flush">
                                  Metal flush hanger
                                </option>
                                <option value="none">No hanger</option>
                              </select>
                              <textarea
                                value={personalization.customer_note || ""}
                                onChange={(event) =>
                                  setPersonalization({
                                    ...personalization,
                                    customer_note: event.target.value,
                                  })
                                }
                                maxLength={
                                  confirmedOrder.personalization_limits
                                    ?.customer_note || 300
                                }
                                placeholder="Optional note"
                                className="cart-inset-field min-h-24 rounded-md p-2.5"
                              />
                              <Button
                                disabled={submitting}
                                onClick={() =>
                                  submitPersonalization(line.line_id)
                                }
                              >
                                Submit personalization
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                </article>
              ))}
            </div>
            <p className="mt-6 text-sm text-stone-400">
              {confirmedOrder.expected_next_update}
            </p>
          </>
        )}
        <div className="mt-8 flex flex-wrap gap-3">
          <Button onClick={() => window.history.back()}>Back</Button>
          <Button
            variant="outline"
            onClick={() => {
              window.location.href =
                searchParams.get("source") === "cart" ? "/gallery" : "/quote";
            }}
          >
            {searchParams.get("source") === "cart"
              ? "Continue shopping"
              : "Return to quote form"}
          </Button>
        </div>
      </div>
    </div>
  );
}
